(ns app.front.view.phase-event.event
  (:require [app.domain.inspection.model :as inspection-model]
            [app.front.ui.status :as ui-status]
            [app.front.view.inspections.subs :as inspection-sub]
            [core.front.event :refer [<sub]]
            [core.front.href :as href]
            [potpuri.core :as p]
            [schema-tools.core :as st]
            [schema.core :as s]))

(s/defschema Event
  (st/merge
    {:event s/Keyword
     :db/id s/Int}
    (st/optional-keys
      {:status     (apply s/enum (keys ui-status/status->border-color-class))
       :href       s/Str
       :time       s/Inst ; optional for imported inspections
       :users      [(s/cond-pre s/Int {:db/id           s/Int
                                       :user/first-name s/Str
                                       :user/last-name  s/Str})]
       :only-date? s/Bool})))

(s/defschema Entity
  (s/pred (fn [e] (-> e :entity/type keyword?)) :domain-entity))

(defmulti -entity->events
  :entity/type)

(s/defn entity->events :- (s/maybe [Event])
  [e :- Entity]
  (-entity->events e))

(defmethod -entity->events
  :default
  [_]
  nil)

(defmethod -entity->events
  :entity.type/workspace
  [{:entity/keys [creator created]
    :as          entity}]
  [(p/assoc-if {:event :phase-event/workspace-created
                :time  created
                :db/id (:db/id entity)}
               :users (some-> creator vector))])

(defmethod -entity->events
  :entity.type/audit-event
  [{:entity/keys      [created creator]
    :audit-event/keys [type new-domain-state]
    :as               entity}]
  (when (= type :audit-event.type/domain-state-change)
    (let [state->event {:execution.state/active-code-enforcement :phase-event/case-active
                        :execution.state/closed-code-enforcement :phase-event/case-closed}]
      (when-let [event (state->event new-domain-state)]
        [{:event event
          :time  created
          :users [(-> creator :db/id)]
          :db/id (:db/id entity)}]))))

(defn- make-inspection-href
  [inspection]
  (href/href :view/inspection-visit
             {:workspace-id  (-> inspection inspection-model/workspace :workspace/id)
              :inspection-id (-> inspection :db/id)}))

(defmethod -entity->events
  :entity.type/inspection
  [{:domain/keys [state] :inspection/keys [due-date] :as inspection}]
  (cond
    (and (= state :inspection.state/active)
         (some? due-date))
    [{:event      :phase-event/inspection-deadline
      :db/id      (:db/id inspection)
      :time       due-date
      :only-date? true
      :status     :task
      :href       (make-inspection-href inspection)}]))

(defmethod -entity->events
  :entity.type/booking
  [{:domain/keys [state] :booking/keys [requested-start-time] :as booking}]
  (let [inspection (-> booking :entity/parent)
        href       (make-inspection-href inspection)]
    (when (-> inspection :domain/state (= :inspection.state/active))
      [(cond-> {:event  :phase-event/inspection-request
                :db/id  (:db/id booking)
                :time   requested-start-time
                :status :task
                :href   href}
         (= state :booking.state/dispatched)
         (assoc :users [(-> booking :booking/assignee :db/id)]))])))

(defmethod -entity->events
  :entity.type/inspection-visit
  [{:inspection.visit/keys [start-time inspectors result visit-result]
    :domain/keys           [state]
    :as                    visit}]
  (let [href         (make-inspection-href (-> visit :entity/parent))
        users        (->> inspectors (map :db/id))
        visits-conf? (<sub [::inspection-sub/visit-configuration-enabled?])]
    (cond
      (= state :inspection.visit.state/in-progress)
      [(p/assoc-if {:event :phase-event/inspection-in-progress
                    :db/id (:db/id visit)
                    :users users
                    :href  href}
                   :time start-time)]

      (= state :inspection.visit.state/done)
      [(p/assoc-if {:event  :phase-event/inspection-visit
                    :db/id  (:db/id visit)
                    :users  users
                    :href   href
                    :status (if visits-conf?
                              (case (:inspection-visit-result/status-type visit-result)
                                :inspection.visit.result-status-type/passed :ok
                                :inspection.visit.result-status-type/failed :critical
                                :inspection.visit.result-status-type/in-progress :warning
                                :inspection.visit.result-status-type/completed :ok
                                :notification)
                              (case result
                                :inspection.visit.result/passed :ok
                                :inspection.visit.result/failed :critical
                                :inspection.visit.result/no-access :warning
                                :inspection.visit.result/not-complete :warning
                                :notification))}
                   :time start-time)])))

(defmethod -entity->events
  :entity.type/inspection-order
  [{:inspection.order/keys [issued-to]
    :domain/keys           [state]
    :entity/keys           [created]
    :as                    order}]
  (when (contains? #{:inspection.order.state/active
                     :inspection.order.state/cleared}
                   state)
    (let [users (some->> issued-to (keep #(-> % :party/user :db/id)) seq vec)]
      [(p/assoc-if {:event :phase-event/inspection-order-issued
                    :time  created
                    :db/id (:db/id order)}
                   :users
                   users)])))

(defmethod -entity->events
  :entity.type/bill
  [{:entity/keys [created] :as bill}]
  [{:event :phase-event/bill-created
    :time  created
    :db/id (:db/id bill)}])

(defmethod -entity->events
  :entity.type/payment
  [{:payment/keys [payer paid-ts]
    :domain/keys  [state]
    :as           payment}]
  (when (= state :payment.state/paid)
    [{:event  :phase-event/bill-payment
      :time   paid-ts
      :users  (some-> payer :party/user :db/id vector)
      :db/id  (:db/id payment)
      :status :ok}]))
